import { useCallback, useEffect, useState } from 'react';
import {
  GetIndustryPipelineFlowCommand,
  getIvtClient,
  type GetIndustryPipelineFlowOutput,
} from '../../client/IvtHubClient';
import { type DenialKind, denialKindOf } from '../../utils/authorization';
import type { WindowRange } from './components/windowPresets';

export interface IndustryFlowState {
  data: GetIndustryPipelineFlowOutput | null;
  loading: boolean;
  error: string | null;
  /**
   * Set when the failure was an authorization denial, so the page can say WHICH kind
   * instead of showing its generic retry alert.
   *
   * ⚠️ Kept separate from `error` rather than folded into it. The page has to make two
   * decisions a message string cannot carry: a 403 must not offer a retry button (the
   * denial is not transient), and an `admin-not-permitted` denial must not be reported
   * as the industry-scope state the page already had — those answer different questions
   * ("may I open this dashboard" vs "which industries do I see"). See DENIAL_HINTS.
   */
  denial: DenialKind | null;
  reload: () => void;
}

/**
 * One call per window for the whole Leader Home page.
 *
 * ⚠️ Deliberately does NOT send `industries`, and deliberately does not call
 * `fetchAllowedIndustries` first — which is a departure from every other IVT list
 * page, so here is why. Those pages resolve the caller's permitted industries
 * client-side because they must never issue an unscoped query. This operation
 * scopes itself: omitting `industries` means "every industry I am permitted to
 * see", and a requested industry outside that set is dropped server-side
 * (verified on beta: `industries=NOPE` returns zero rows rather than widening).
 * Resolving permissions here as well would create a second source of truth that
 * can disagree with the server's.
 *
 * The cost is that an empty `industries` array cannot distinguish "no permission"
 * from "no data" — but a permitted caller always gets a row per industry in scope,
 * including all-zero rows, so in practice empty means not permitted. The page
 * words that state accordingly.
 *
 * ⚠️ That empty-array state is about INDUSTRY SCOPE and is reached on a 200. It is not
 * the same thing as `denial`, which is a 403 and means the caller may not open this
 * dashboard at all (DAT-8997 restricted the GM view to `ivthub-admin`). Both exist and
 * the page must keep them apart: one says "you see no industries", the other "this page
 * is not yours".
 */
export function useIndustryFlow(range: WindowRange, enabled = true): IndustryFlowState {
  const [data, setData] = useState<GetIndustryPipelineFlowOutput | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [denial, setDenial] = useState<DenialKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { startDate, endDate } = range;

  useEffect(() => {
    let cancelled = false;
    // ⚠️ GM is ADMIN_ONLY. Without this gate the page would fire it — and take a 403 — for
    // every non-admin reading the STO view, which is the majority of this page's audience.
    // `loading` is cleared rather than left as it was: switching away mid-flight cancels the
    // in-flight settle, which would otherwise strand it at true.
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // ⚠️ `denial` is deliberately NOT cleared here. Clearing it at request start is what let
    // the refused figures come back: nothing drops `data` on a denial, so for the whole
    // duration of the NEXT request `noAccess` was false with the 403'd response still cached,
    // the `!noAccess` content gate re-opened, and the KPI band and table rendered again —
    // announced by the role="status" region as 更新中…, i.e. as live data. It is cleared on
    // success instead, so a denial outlives the in-flight refetch that follows it.
    (async () => {
      try {
        const out = await getIvtClient().send(
          new GetIndustryPipelineFlowCommand({ startDate, endDate }),
        );
        if (!cancelled) {
          setData(out);
          setDenial(null);
        }
      } catch (e) {
        // Keep the previous data on screen rather than blanking the page: the
        // window selector is the usual trigger, and losing the old numbers on a
        // transient failure reads as "the data went away".
        // Classified BEFORE the message is flattened: `denialKindOf` reads `reason`,
        // `name` and `$metadata.httpStatusCode` off the thrown object, none of which
        // survive `(e as Error).message`.
        if (!cancelled) {
          const kind = denialKindOf(e);
          setDenial(kind);
          // A denial also DROPS the cached response. Keeping the last 200 is right for a
          // transient failure — that is what stops a window switch blanking the page — and
          // wrong for a 403: it is the cache that made the refused numbers reachable at all,
          // and it also covers the 403-then-500 variant, where the generic retry alert would
          // otherwise render over them.
          if (kind !== null) setData(null);
          setError((e as Error).message ?? 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, reloadKey, enabled]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, denial, reload };
}
